/**
 * YouTubeTeamWorkspace — multi-agent collaboration UI for YouTube paid ads.
 *
 * Shows the orchestrator's plan + each specialist's findings/recs as
 * collapsible panels. Approval gates surface as actionable cards.
 * Ad-hoc question box at the top routes through the orchestrator.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Youtube, Play, Loader2, RefreshCw, ChevronDown, ChevronRight,
  Send, CheckCircle2, XCircle, AlertTriangle, Bot, Target, Users,
  Film, TrendingUp, BarChart3, Wrench, Sparkles
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { authFetch } from '../lib/api';

const API = process.env.REACT_APP_BACKEND_URL;

const SPECIALIST_ICONS = {
  yt_strategy: Target,
  yt_audience: Users,
  yt_creative: Film,
  yt_bidding: TrendingUp,
  yt_measurement: BarChart3,
  yt_troubleshooter: Wrench,
};

const IMPACT_COLOR = {
  critical: 'bg-red-100 text-red-800 border-red-200',
  high: 'bg-orange-100 text-orange-800 border-orange-200',
  medium: 'bg-amber-100 text-amber-800 border-amber-200',
  low: 'bg-slate-100 text-slate-700 border-slate-200',
};

const SpecialistPanel = ({ specialist, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const Icon = SPECIALIST_ICONS[specialist.agent_id] || Bot;
  const findings = specialist.findings || [];
  const recs = specialist.recommendations || [];

  return (
    <Card className="overflow-hidden" data-testid={`yt-specialist-${specialist.agent_id}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 p-4 hover:bg-surface-page text-left transition-colors"
      >
        <div className="w-9 h-9 rounded-lg bg-chart-purple/10 flex items-center justify-center flex-shrink-0">
          <Icon size={16} className="text-chart-purple" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-text-primary">{specialist.agent_name}</p>
          <p className="text-xs text-text-secondary line-clamp-1">{specialist.summary}</p>
        </div>
        <div className="flex items-center gap-2 text-xs">
          {findings.length > 0 && <span className="font-semibold text-orange-600">{findings.length}f</span>}
          {recs.length > 0 && <span className="font-semibold text-brand">{recs.length}r</span>}
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </div>
      </button>

      {open && (
        <CardContent className="pt-0 pb-4 px-4 space-y-3 border-t border-border-subtle">
          {findings.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-text-muted mb-2 mt-3">Findings</p>
              <div className="space-y-2">
                {findings.map((f) => (
                  <div key={f.id} className="p-3 rounded-md bg-surface-page border border-border-subtle">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <p className="text-sm font-medium text-text-primary">{f.title}</p>
                      <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded border ${IMPACT_COLOR[f.impact] || IMPACT_COLOR.medium}`}>
                        {f.impact}
                      </span>
                    </div>
                    <p className="text-xs text-text-secondary">{f.description}</p>
                    {f.evidence && (
                      <p className="text-[11px] text-text-tertiary font-mono mt-1.5">{f.evidence}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          {recs.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-text-muted mb-2 mt-3">Recommendations</p>
              <div className="space-y-2">
                {recs.map((r) => (
                  <div key={r.id} className="p-3 rounded-md bg-brand/5 border border-brand/20">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <p className="text-sm font-medium text-text-primary">{r.action}</p>
                      <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded border bg-brand/10 text-brand border-brand/20">
                        {r.priority}
                      </span>
                    </div>
                    <p className="text-xs text-text-secondary">{r.reasoning}</p>
                    {r.expected_outcome && (
                      <p className="text-[11px] text-text-tertiary mt-1.5 italic">→ {r.expected_outcome}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};

const GateCard = ({ gate, onDecide }) => {
  const [deciding, setDeciding] = useState(null);
  const decide = async (decision) => {
    setDeciding(decision);
    try {
      await onDecide(gate.id, decision);
    } finally {
      setDeciding(null);
    }
  };
  const isResolved = gate.status === 'approved' || gate.status === 'rejected';

  return (
    <div className={`p-4 rounded-md border ${isResolved ? 'bg-surface-page border-border-subtle opacity-60' : 'bg-amber-50 border-amber-200'}`} data-testid={`yt-gate-${gate.id || gate.campaign_id}`}>
      <div className="flex items-start gap-3">
        <AlertTriangle size={18} className={`flex-shrink-0 mt-0.5 ${isResolved ? 'text-text-muted' : 'text-amber-600'}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <p className="text-[10px] uppercase font-bold tracking-wider text-amber-800">
              {gate.type.replace(/_/g, ' ')}
            </p>
            {isResolved && (
              <span className="text-[10px] font-semibold uppercase text-text-muted">{gate.status}</span>
            )}
          </div>
          <p className="text-sm font-semibold text-text-primary mb-0.5">{gate.campaign_name}</p>
          <p className="text-xs text-text-secondary">{gate.reasoning}</p>
          {gate.proposed_budget && (
            <p className="text-[11px] text-text-tertiary mt-1">
              Budget: ${gate.current_budget?.toFixed(0)} → ${gate.proposed_budget?.toFixed(0)}/day (+20%)
            </p>
          )}
          {!isResolved && (
            <div className="flex gap-2 mt-3">
              <Button size="sm" onClick={() => decide('approve')} disabled={!!deciding} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`gate-approve-${gate.id}`}>
                {deciding === 'approve' ? <Loader2 size={12} className="animate-spin mr-1" /> : <CheckCircle2 size={12} className="mr-1" />}
                Approve
              </Button>
              <Button size="sm" variant="outline" onClick={() => decide('reject')} disabled={!!deciding} data-testid={`gate-reject-${gate.id}`}>
                {deciding === 'reject' ? <Loader2 size={12} className="animate-spin mr-1" /> : <XCircle size={12} className="mr-1" />}
                Reject
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const YouTubeTeamWorkspace = () => {
  const [team, setTeam] = useState(null);
  const [run, setRun] = useState(null);
  const [running, setRunning] = useState(false);
  const [question, setQuestion] = useState('');
  const [askLoading, setAskLoading] = useState(false);
  const [answer, setAnswer] = useState(null);

  // Load team metadata
  useEffect(() => {
    (async () => {
      try {
        const res = await authFetch(`${API}/api/youtube/team`);
        if (res.ok) setTeam(await res.json());
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to load YouTube team:', e);
      }
    })();
  }, []);

  const runTeam = useCallback(async () => {
    setRunning(true);
    try {
      const res = await authFetch(`${API}/api/youtube/run`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ account_id: 'demo' }),
      });
      if (!res.ok) throw new Error('Team run failed');
      setRun(await res.json());
      toast.success('YouTube team run complete');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setRunning(false);
    }
  }, []);

  const decideGate = useCallback(async (gateId, decision) => {
    try {
      const res = await authFetch(`${API}/api/youtube/gates/${gateId}/decide`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ decision }),
      });
      if (!res.ok) throw new Error('Gate decision failed');
      toast.success(`Gate ${decision}d`);
      // Update local state
      setRun((prev) => prev ? {
        ...prev,
        gates: (prev.gates || []).map((g) => g.id === gateId ? { ...g, status: decision + 'd' } : g),
      } : prev);
    } catch (e) {
      toast.error(e.message);
    }
  }, []);

  const askTeam = async () => {
    const q = question.trim();
    if (!q) return;
    setAskLoading(true);
    setAnswer(null);
    try {
      const res = await authFetch(`${API}/api/youtube/ask`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: q, account_id: 'demo' }),
      });
      if (!res.ok) throw new Error('Ask failed');
      setAnswer(await res.json());
    } catch (e) {
      toast.error(e.message);
    } finally {
      setAskLoading(false);
    }
  };

  const specialists = run ? Object.values(run.specialists || {}) : [];
  const gates = run?.gates || [];

  return (
    <div className="space-y-6 max-w-6xl mx-auto p-6" data-testid="youtube-team-workspace">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Youtube size={22} className="text-red-600" />
            <h1 className="text-2xl font-bold text-text-primary">YouTube Agentic Team</h1>
            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200">
              Demo Mode
            </span>
          </div>
          <p className="text-sm text-text-tertiary mt-1">
            6 specialists + an Orchestrator collaborating on your YouTube paid ads strategy
          </p>
        </div>
        <Button onClick={runTeam} disabled={running} className="bg-brand hover:bg-brand-hover" data-testid="yt-run-team">
          {running ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Play size={14} className="mr-1.5" />}
          {running ? 'Running…' : run ? 'Re-run Team' : 'Run Team'}
        </Button>
      </div>

      {/* Team metadata (when no run yet) */}
      {!run && team && (
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-text-secondary mb-4">
              Click <strong>Run Team</strong> to kick off the workflow. The orchestrator will run all 6 specialists in parallel,
              synthesize their findings into an executive plan, and surface any decisions that need your approval.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {team.specialists.map((s) => {
                const Icon = SPECIALIST_ICONS[s.id] || Bot;
                return (
                  <div key={s.id} className="p-3 rounded-md border border-border-subtle bg-surface-page flex items-center gap-3">
                    <div className="w-8 h-8 rounded-md bg-chart-purple/10 flex items-center justify-center">
                      <Icon size={14} className="text-chart-purple" />
                    </div>
                    <p className="text-sm font-medium text-text-primary">{s.name}</p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Ad-hoc question box */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles size={14} className="text-chart-purple" />
            <p className="text-sm font-semibold text-text-primary">Ask the team</p>
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && askTeam()}
              placeholder='e.g. "Why is my Demand Gen CPA climbing this week?"'
              className="flex-1 px-3 py-2 text-sm border border-border-subtle rounded-md focus:outline-none focus:border-brand"
              data-testid="yt-ask-input"
            />
            <Button onClick={askTeam} disabled={!question.trim() || askLoading} className="bg-chart-purple hover:bg-chart-purple/90 text-white" data-testid="yt-ask-btn">
              {askLoading ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            </Button>
          </div>
          {answer && (
            <div className="mt-3 p-3 rounded-md bg-chart-purple/5 border border-chart-purple/20">
              <p className="text-[10px] uppercase tracking-wider text-text-muted mb-1">Orchestrator answer</p>
              <p className="text-sm text-text-primary whitespace-pre-wrap">{answer.answer}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Run results */}
      {run && (
        <>
          {/* Orchestrator plan */}
          <Card className="border-chart-purple/30">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <Bot size={16} className="text-chart-purple" />
                <p className="text-sm font-bold uppercase tracking-wider text-chart-purple">Orchestrator's Weekly Plan</p>
              </div>
              <p className="text-sm text-text-primary whitespace-pre-wrap leading-relaxed">
                {run.plan?.executive_summary || run.summary}
              </p>
            </CardContent>
          </Card>

          {/* Approval gates */}
          {gates.length > 0 && (
            <div>
              <p className="text-sm font-semibold uppercase tracking-wider text-text-muted mb-3">Approval Gates</p>
              <div className="space-y-2">
                {gates.map((g, i) => <GateCard key={g.id || i} gate={g} onDecide={decideGate} />)}
              </div>
            </div>
          )}

          {/* Specialist outputs */}
          <div>
            <p className="text-sm font-semibold uppercase tracking-wider text-text-muted mb-3">Specialist Reports</p>
            <div className="space-y-2">
              {specialists.map((s, i) => <SpecialistPanel key={s.agent_id} specialist={s} defaultOpen={i === 0} />)}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default YouTubeTeamWorkspace;
